import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminUser, updateAdminUser } from '../../api/enrollments';
import StatusBadge from '../../components/StatusBadge';
import type { EnrollmentStatus } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—';
  return format(new Date(dateStr), "d MMM yyyy 'alle' HH:mm", { locale: it });
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ nome: '', cognome: '', email: '', azienda: '' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => getAdminUser(id!),
    enabled: !!id,
    onSuccess: (d: any) => {
      setFormData({
        nome: d.user.nome ?? '',
        cognome: d.user.cognome ?? '',
        email: d.user.email ?? '',
        azienda: d.user.azienda ?? '',
      });
    },
  } as any);

  const mutation = useMutation({
    mutationFn: (values: typeof formData) =>
      updateAdminUser(id!, {
        nome: values.nome,
        cognome: values.cognome,
        email: values.email,
        azienda: values.azienda || null,
      }),
    onSuccess: () => {
      setSaveSuccess(true);
      setSaveError(null);
      setEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.error ?? 'Errore durante il salvataggio');
    },
  });

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="alert alert-danger">Utente non trovato</div>
        <button onClick={() => navigate('/admin/users')} className="btn btn-secondary">
          Torna agli iscritti
        </button>
      </div>
    );
  }

  const { user, enrollments } = data as any;
  const activeEnrollments = enrollments.filter((e: any) => e.status !== 'cancelled');
  const cancelledEnrollments = enrollments.filter((e: any) => e.status === 'cancelled');

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/admin/users')} className="btn btn-ghost btn-sm">
            ← Indietro
          </button>
          <div>
            <h1 className="page-title" style={{ fontSize: '1.25rem' }}>
              {user.nome} {user.cognome}
            </h1>
            <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: '0.875rem' }}>
              {user.email}
            </p>
          </div>
        </div>
        <span
          style={{
            fontSize: '0.875rem',
            color: 'var(--gray-500)',
            background: 'var(--gray-100)',
            borderRadius: '6px',
            padding: '0.25rem 0.75rem',
          }}
        >
          {activeEnrollments.length} iscrizioni attive
        </span>
      </div>

      {saveError && (
        <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{saveError}</div>
      )}
      {saveSuccess && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>Dati aggiornati con successo</div>
      )}

      {/* Dati utente */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Dati partecipante</h3>
          {!editMode && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setFormData({
                  nome: user.nome ?? '',
                  cognome: user.cognome ?? '',
                  email: user.email ?? '',
                  azienda: user.azienda ?? '',
                });
                setEditMode(true);
                setSaveError(null);
              }}
            >
              Modifica
            </button>
          )}
        </div>

        {editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Nome</label>
              <input
                className="form-control"
                value={formData.nome}
                onChange={(e) => setFormData((f) => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome</label>
              <input
                className="form-control"
                value={formData.cognome}
                onChange={(e) => setFormData((f) => ({ ...f, cognome: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-control"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Azienda</label>
              <input
                className="form-control"
                value={formData.azienda}
                onChange={(e) => setFormData((f) => ({ ...f, azienda: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.75rem' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => mutation.mutate(formData)}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Salvataggio...' : 'Salva modifiche'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setEditMode(false); setSaveError(null); }}
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: '1rem', padding: '0.625rem 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ minWidth: '160px', fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: 500 }}>Nome completo</span>
              <span>{user.nome} {user.cognome}</span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', padding: '0.625rem 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ minWidth: '160px', fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: 500 }}>Email</span>
              <span>{user.email}</span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', padding: '0.625rem 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ minWidth: '160px', fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: 500 }}>Azienda</span>
              <span>{user.azienda ?? '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', padding: '0.625rem 0' }}>
              <span style={{ minWidth: '160px', fontSize: '0.875rem', color: 'var(--gray-500)', fontWeight: 500 }}>Registrato il</span>
              <span>{formatDate(user.created_at)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Iscrizioni attive */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Iscrizioni attive ({activeEnrollments.length})</h3>
        </div>
        {activeEnrollments.length === 0 ? (
          <div style={{ padding: '1.5rem', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
            Nessuna iscrizione attiva
          </div>
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Corso</th>
                  <th>Edizione</th>
                  <th>Data sessione</th>
                  <th>Stato</th>
                  <th>Iscritto il</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {activeEnrollments.map((enr: any) => (
                  <tr key={enr.id}>
                    <td style={{ fontWeight: 500 }}>{enr.course_name}</td>
                    <td style={{ color: 'var(--gray-500)' }}>{enr.edition_label}</td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                      {enr.scheduled_at ? format(new Date(enr.scheduled_at), 'd MMM yyyy', { locale: it }) : '—'}
                    </td>
                    <td>
                      <StatusBadge status={enr.status as EnrollmentStatus} />
                    </td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                      {enr.registered_at ? format(new Date(enr.registered_at), 'd MMM yyyy', { locale: it }) : '—'}
                    </td>
                    <td>
                      <Link to={`/admin/enrollments/${enr.id}`} className="btn btn-ghost btn-sm">
                        Dettaglio
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Iscrizioni annullate */}
      {cancelledEnrollments.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ color: 'var(--gray-500)' }}>
              Iscrizioni annullate ({cancelledEnrollments.length})
            </h3>
          </div>
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Corso</th>
                  <th>Edizione</th>
                  <th>Annullato il</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cancelledEnrollments.map((enr: any) => (
                  <tr key={enr.id} style={{ opacity: 0.6 }}>
                    <td>{enr.course_name}</td>
                    <td style={{ color: 'var(--gray-500)' }}>{enr.edition_label}</td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                      {enr.cancelled_at ? format(new Date(enr.cancelled_at), 'd MMM yyyy', { locale: it }) : '—'}
                    </td>
                    <td>
                      <Link to={`/admin/enrollments/${enr.id}`} className="btn btn-ghost btn-sm">
                        Dettaglio
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
